/**
 * Aanwezigheidskalender — dag-cel (Epic 4, Story 4.2, UX V6)
 *
 * Renders one employee's ochtend+middag for one date. Per-dagdeel status is
 * independent (AD-5 dagdeel-precisie) — a split day (e.g. ochtend werkt,
 * middag verlof) renders as two stacked halves; an identical
 * morning/afternoon renders as one merged block (matches
 * `PlanningDayCell.tsx`'s existing half-day-split precedent).
 *
 * Accessibility (UX Cross-cutting, bindend): every half carries a visible
 * text label next to its color/pattern/icon — never color-only.
 */

import type { AttendanceDagdeelEntry } from '@functieloonhuis/shared';
import {
  leaveNameFor,
  statusLabel,
  VRIJ_COLOR,
  HOLIDAY_BG,
  HOLIDAY_BORDER,
  HOLIDAY_COLOR,
  ZIEK_STRIPE_LIGHT,
  ZIEK_STRIPE_DARK,
  ZIEK_TEXT,
  KANTOOR_BG,
  KANTOOR_ACCENT,
  KANTOOR_TEXT,
  THUIS_BG,
  THUIS_ACCENT,
  THUIS_TEXT,
  hasNonStandardSpan,
  durationBar,
  dagdeelTimeLabel,
  pastelTint,
  readableInk,
  LEAVE_PASTEL_AMOUNT,
  rosterHoursFromSpan,
  werkVrijSplit,
  kortVerlofLabel,
  kortVerlofAriaFragment,
  kortVerlofPositionBar,
  kortVerlofTijdvakLabel,
  kortVerlofTijdvakAriaFragment,
} from './aanwezigheidVisuals';
import { EhboBadge } from './BhvBadge';

interface AanwezigheidCellProps {
  employeeName: string;
  morning: AttendanceDagdeelEntry;
  afternoon: AttendanceDagdeelEntry;
  holidayName?: string | null;
  /**
   * Epic ux-aanwezigheidscel-duur, Story 1.3 (CAP-4): drives the
   * cel-niveau EHBO-teken (BHV-dekkingssignaal) — only rendered on a
   * `werkt`-dagdeel (see `HalfCell`'s `werkt`-case), never elsewhere.
   * Optional/defaults to falsy so existing callers that don't pass it
   * (e.g. older tests) keep rendering without the badge.
   */
  isBhv?: boolean;
  /**
   * Epic kort-verlof-duur, Story 1.1 (CAP-2): dag-niveau kort-verlof-duur
   * (uren) — mirrors `AttendanceDayEntry.kortVerlofUren` (shared type
   * doc-comment). Deliberately NOT part of `morning`/`afternoon`: a kort
   * verlof (bv. een tandartsbezoek) heeft geen dagdeel-positie, alleen een
   * totaal-duur over de dag. Optional/defaults to falsy so existing
   * callers that don't pass it keep rendering unchanged (no annotation).
   */
  kortVerlofUren?: number | null;
  /**
   * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (AD-11, Structural
   * Seed regel 446): het omvattende tijdvak van het kort verlof, "HH:MM" —
   * mirrors `AttendanceDayEntry.kortVerlofStart`/`kortVerlofEind`. Optional/
   * nullable, net als `kortVerlofUren` destijds, zodat bestaande aanroepers
   * (en tests) die deze twee props niet meesturen ongewijzigd blijven
   * werken — die vallen terug op het bestaande duur-pad (AC-4/AC-9).
   */
  kortVerlofStart?: string | null;
  kortVerlofEind?: string | null;
}

function sameDagdeel(a: AttendanceDagdeelEntry, b: AttendanceDagdeelEntry): boolean {
  return a.status === b.status && a.location === b.location && a.leaveType === b.leaveType;
}

/**
 * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 4): the roster
 * clock-time span ("HH:MM"-"HH:MM") for a `werkt`-dagdeel, or `null` when
 * either edge is missing — same source fields and same guard shape as
 * `rosterHoursFromSpan` (only `startTime`/`endTime` are set for `werkt`,
 * shared type doc-comment), kept as a separate helper because
 * `kortVerlofPositionBar` needs the raw "HH:MM" strings, not a derived
 * hour-count.
 */
function rosterSpanFor(entry: AttendanceDagdeelEntry): { start: string; end: string } | null {
  return entry.startTime && entry.endTime ? { start: entry.startTime, end: entry.endTime } : null;
}

/**
 * Exhaustive per-status render (AC 7 / adversarial review finding 4):
 * a `switch` with an explicit case for every `AttendanceDagdeelStatus`
 * value, ending in a `default` that both fails TypeScript compilation if
 * a future status is added without a case here (the `_exhaustiveCheck:
 * never` assignment) AND throws at runtime for any value that bypasses
 * the type system (e.g. a stale cached response). Previously this was a
 * chain of `if`s ending in an unconditioned "werkt" default — a missing/
 * misplaced status branch would have silently rendered as "Kantoor"
 * (false-positive "aanwezig"), which is exactly what this guards against.
 */
function HalfCell({
  entry,
  holidayName,
  isBhv,
  isSplitHalf,
  suppressDurationVisuals,
}: {
  entry: AttendanceDagdeelEntry;
  holidayName?: string | null;
  isBhv?: boolean;
  /**
   * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-1, AC 17, review-
   * bevinding 8): whether this half renders inside a SPLIT (20px,
   * ochtend/middag gestapeld) cell rather than a merged hele-dag cell.
   * A split half hides the VISIBLE tijd-badge (te krap) — the tijd blijft
   * wel in de `aria-label` (`dagdeelAriaFragment` below is unaffected).
   * The duur-balk itself may still render in a split half.
   */
  isSplitHalf?: boolean;
  /**
   * Epic kort-verlof-duur, Story 1.1 (CAP-2, review-bevinding 6, "Samenloop
   * met verkorte dag"): when the day carries a `kortVerlofUren`
   * annotation, `AanwezigheidCell` renders ITS OWN dag-niveau gestapelde
   * werk+vrij-balk + `Xu vrij`-badge (spanning the full cell — needed for
   * a gesplitste cel too, review-bevinding 3), so this half must NOT also
   * render its own plain accent-only duur-balk/tijd-badge — that would
   * visually double up. The tijd blijft beschikbaar via
   * `dagdeelAriaFragment` (unaffected by this flag); alleen het ZICHTBARE
   * tijd-badge wijkt voor het kort-verlof-badge bij samenloop.
   */
  suppressDurationVisuals?: boolean;
}) {
  const label = statusLabel(entry.status);

  switch (entry.status) {
    case 'feestdag':
      return (
        <div
          className="flex items-center justify-center gap-1 h-full px-1"
          style={{ backgroundColor: HOLIDAY_BG, borderColor: HOLIDAY_BORDER }}
        >
          <span className="text-[9px] font-medium truncate" style={{ color: HOLIDAY_COLOR }}>
            {holidayName ?? label}
          </span>
        </div>
      );

    case 'ziek':
      // Epic ux-aanwezigheidscel-duur, Story 1.2 (CAP-2, Open Decision 2):
      // herstyled van massief-navy naar een gedempte, navy-getinte
      // diagonale arcering (zelfde stijl-familie als
      // `niet_werkbaar_patroon`) — "afwezig treedt terug" in het
      // nadruk-model. Rood (#D64545) blijft uitgesloten, gereserveerd voor
      // het EHBO-teken (Story 1.3). Geen location/leaveType op deze status
      // (net als feestdag/weekend).
      //
      // Epic aanwezigheidscel-iteratie2, Story 1.3 (CAP-5, AC 1/AC 3):
      // het medisch-kruis-icoon las als een klikbaar "+"/toevoegen-teken
      // en is VERWIJDERD (component + export volledig weg) — `ziek` = alleen
      // de gedempte diagonale arcering + het tekstlabel "Ziek". A11y
      // blijft OK: patroon (arcering) + tekstlabel + `aria-label` zijn
      // samen voldoende (nooit-alleen-kleur), geen icoon nodig.
      return (
        <div
          className="flex items-center justify-center gap-1 h-full px-1"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, ${ZIEK_STRIPE_LIGHT}, ${ZIEK_STRIPE_LIGHT} 4px, ${ZIEK_STRIPE_DARK} 4px, ${ZIEK_STRIPE_DARK} 8px)`,
          }}
          title={label}
        >
          <span className="text-[9px] font-medium truncate" style={{ color: ZIEK_TEXT }}>
            {label}
          </span>
        </div>
      );

    case 'weekend':
      return (
        <div className="flex items-center justify-center h-full bg-[#F3F4F6]">
          <span className="text-[9px] text-gray-400">{label}</span>
        </div>
      );

    case 'niet_werkbaar_patroon':
      // Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-2, AC 1-3): renders
      // EMPTY — geen icoon, geen label, geen arceringspatroon (dit
      // VERVANGT de oude "Structureel vrij"-arcering+label). Betekenis
      // draagt uitsluitend via de cel-`aria-label`
      // (`statusLabel('niet_werkbaar_patroon')` = "Niet ingeroosterd",
      // zie `dagdeelAriaFragment` hieronder) — nooit alleen kleur/leegte
      // zonder een aria-tekst (a11y hard, tech-spec Constraints).
      //
      // Bewuste asymmetrie (AC 6, review-bevinding 11): weekend/feestdag
      // (de twee cases hierboven) behouden hun bestaande gelabelde
      // weergave — ALLEEN een persoonlijke rooster-niet-werkdag wordt
      // leeg. Dit is een expliciet besluit van Friso, niet een
      // inconsistentie die later "gefixt" moet worden.
      return <div className="h-full bg-white" />;

    case 'verlof_goedgekeurd': {
      // Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-3, AC 7/8/10):
      // "verlof treedt terug" — een lichte pasteltint (i.p.v. de
      // volledig-verzadigde achtergrond+wit label) met een gedempt,
      // leesbaar (AA-gegarandeerd) donker label.
      //
      // Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-1/CAP-2): élk
      // goedgekeurd-verlof-dagdeel toont voortaan hetzelfde label "Vrij"
      // (`leaveNameFor` retourneert altijd "Vrij") op dezelfde uniforme
      // `VRIJ_COLOR`-pastel — geen per-verloftype-kleur meer
      // (`leaveColorFor` is verwijderd). Het peer-pad (`leaveType` null)
      // krijgt automatisch dezelfde behandeling, geen aparte tak nodig.
      const name = leaveNameFor(entry.leaveType);
      const bg = pastelTint(VRIJ_COLOR, LEAVE_PASTEL_AMOUNT);
      const ink = readableInk(VRIJ_COLOR);
      return (
        <div
          className="flex items-center justify-center h-full px-1"
          style={{ backgroundColor: bg }}
          title={name}
        >
          <span className="text-[9px] truncate" style={{ color: ink, fontWeight: 500 }}>
            {name}
          </span>
        </div>
      );
    }

    // Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4, AC 5, review-
    // bevinding 1): `verlof_in_afwachting` MUST stay its own explicit
    // `case` (removing it would break the `never`-exhaustiveness check
    // below — the status remains in the shared union), but it now falls
    // through to the EXACT SAME render as `werkt` — a defensive
    // type-backstop only, the kalender-resolver never produces this status
    // anymore (see the leave-query fix, calendarService.ts regel ~293).
    // The EHBO-teken stays gated on the literal `entry.status === 'werkt'`
    // check below (not on this case-group), so a defensively-encountered
    // `verlof_in_afwachting` entry never shows the BHV-dekkingssignaal.
    case 'verlof_in_afwachting':
    case 'werkt': {
      // Epic ux-aanwezigheidscel-duur, Story 1.2 (CAP-2/CAP-3): "werk
      // springt eruit" — precomputed static hex-tints applied via inline
      // `style` (no `color-mix()`, no Tailwind arbitrary-class — a typfout
      // there would be a silent no-op, adversarial review bevinding 6).
      const isKantoor = entry.location !== 'thuis';
      const bg = isKantoor ? KANTOOR_BG : THUIS_BG;
      const accent = isKantoor ? KANTOOR_ACCENT : THUIS_ACCENT;
      const textColor = isKantoor ? KANTOOR_TEXT : THUIS_TEXT;
      const locationLabel = isKantoor ? 'Kantoor' : 'Thuis';

      // Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-1): "rust in de
      // standaard, nadruk op de afwijking" — de duur-balk rendert voor
      // ELKE afwijking van de standaard-spanne (`hasNonStandardSpan`,
      // gedeeld met `dagdeelAriaFragment` hieronder zodat de aria-label
      // nooit tijden draagt die de zichtbare weergave niet ook toont),
      // ÓÓK een spanne LANGER dan standaard (AC 15) — die krijgt alleen
      // geen zichtbaar tekst-label (`dagdeelTimeLabel` retourneert dan
      // `null`), de balk zelf toont wél (geclampt/vol).
      const { startTime, endTime } = entry;
      const showBar = Boolean(startTime && endTime) && hasNonStandardSpan(entry) && !suppressDurationVisuals;
      const timeLabel = startTime && endTime ? dagdeelTimeLabel(startTime, endTime) : null;
      // Review-bevinding 8 (AC 17): een gesplitste halve cel (20px) is te
      // krap voor een zichtbaar tijd-badge — laat het weg, de tijd blijft
      // in de aria-label. De duur-balk (2px) mag in een split-half wél.
      // Epic kort-verlof-duur, Story 1.1 (review-bevinding 6): bij
      // samenloop met een kort-verlof-annotatie wint het kort-verlof-badge
      // (`AanwezigheidCell` rendert dat zelf) — dit tijd-badge wijkt dan.
      const showTimeBadge = timeLabel !== null && !isSplitHalf && !suppressDurationVisuals;
      const barGeometry = showBar && startTime && endTime ? durationBar(startTime, endTime) : null;

      return (
        <div
          className="relative flex items-center justify-center gap-1 h-full px-1 overflow-hidden"
          style={{ backgroundColor: bg, boxShadow: `inset 3px 0 0 ${accent}` }}
          title={locationLabel}
        >
          <LocationIcon kantoor={isKantoor} color={accent} />
          <span
            className={`text-[9px] truncate ${isBhv && entry.status === 'werkt' ? 'mr-3' : ''}`}
            style={{ color: textColor, fontWeight: 700 }}
          >
            {locationLabel}
          </span>
          {showTimeBadge && (
            <span
              className="absolute bottom-0.5 right-1 text-[7px] font-medium leading-none"
              style={{ color: textColor }}
            >
              {timeLabel}
            </span>
          )}
          {barGeometry && (
            <span
              aria-hidden="true"
              className="absolute bottom-0 h-0.5"
              style={{
                backgroundColor: accent,
                left: `${barGeometry.left * 100}%`,
                width: `${barGeometry.width * 100}%`,
              }}
            />
          )}
          {/* Epic ux-aanwezigheidscel-duur, Story 1.3 (CAP-4): the
              EHBO-teken renders per gewerkt dagdeel (developer choice, AC 5
              Open Decision 3 — a split cell shows it on the werkt-half
              only, since `HalfCell` already runs independently per
              dagdeel; no extra celniveau-aggregation needed). Absolutely
              positioned top-right so it never competes with the tijd-badge
              (bottom-right, AC 6) and is never squeezed out of the flex
              layout by a long/truncating label (AC 7 — dekking is het
              belangrijkste signaal, blijft altijd staan).
              Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4): gated
              explicitly on `entry.status === 'werkt'` (not just `isBhv`)
              now that this render block is shared with the defensive
              `verlof_in_afwachting` case above — the EHBO-dekkingssignaal
              must never appear for a non-werkt status, defensive or not.
              Epic aanwezigheidscel-iteratie2, Story 1.3 (CAP-6, AC 9): the
              gecentreerde label krijgt bewust een kleine rechter-marge
              (`mr-3`, boven) wanneer het EHBO-teken tegelijk rendert — de
              "nette variant" uit de tech-spec — zodat het teken op de
              smalste kolombreedte nooit over het label heen valt, ook al
              is het teken zelf al kleiner (12px, was 16px). */}
          {isBhv && entry.status === 'werkt' && <EhboBadge />}
        </div>
      );
    }

    default: {
      // Unreachable for any value in the current AttendanceDagdeelStatus
      // union — this assignment fails `tsc` if a future status is added
      // without a case above. The throw is the runtime backstop for a
      // value that bypasses the type system (e.g. a stale cached
      // response from before a status was added).
      const _exhaustiveCheck: never = entry.status;
      throw new Error(`AanwezigheidCell.HalfCell: onbekende dagdeel-status "${String(_exhaustiveCheck)}"`);
    }
  }
}

/**
 * `color` (a static hex constant) applies via inline `style` — same "no
 * dynamic Tailwind arbitrary-class" discipline as the nadruk-tokens
 * themselves (adversarial review, bevinding 6). `colorClass` remains for
 * pre-existing static-Tailwind-class callers.
 */
function LocationIcon({ kantoor, colorClass, color }: { kantoor: boolean; colorClass?: string; color?: string }) {
  const className = `h-3 w-3 flex-shrink-0 ${color ? '' : (colorClass ?? '')}`;
  const style = color ? { color } : undefined;
  return kantoor ? (
    <svg aria-hidden="true" className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
    </svg>
  ) : (
    <svg aria-hidden="true" className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3" />
    </svg>
  );
}

/**
 * Epic ux-aanwezigheidscel-duur, Story 1.2 (CAP-3, AC 11 + tech-spec
 * Constraints "aria-label bevat naam/dag/status/locatie/tijden"): for a
 * `werkt`-dagdeel, the accessible name carries the location (kantoor/
 * thuis) and — for a halve dag or a verkorte dag — the roster tijden, so a
 * screen-reader user gets the same "waar, en hoe lang" information the
 * nadruk-model/duur-badge convey visually. Every other status keeps using
 * the plain `statusLabel` (unchanged, AC 3 — those statuses aren't part
 * of this story's duur-weergave).
 */
function dagdeelAriaFragment(entry: AttendanceDagdeelEntry): string {
  // Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4, AC 5): the
  // `verlof_in_afwachting` defensive backstop shares this location-based
  // fragment with `werkt` too — "identiek aan de werkt-case" (tech-spec
  // CAP-4) explicitly includes the aria-tekst-pad, not just the visible
  // render. `buildDagdeelEntry` still carries `location` for this status
  // (unaffected by this story), but never `startTime`/`endTime`/
  // `isHalfDay`, so the tijden-suffix below naturally stays absent here.
  if (entry.status !== 'werkt' && entry.status !== 'verlof_in_afwachting') return statusLabel(entry.status);

  const isKantoor = entry.location !== 'thuis';
  const isHalfDay = entry.isHalfDay && entry.halfDayPeriod;
  let fragment = isHalfDay ? `halve dag ${entry.halfDayPeriod}` : isKantoor ? 'kantoor' : 'thuis';

  // Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-1): `hasNonStandardSpan`
  // REPLACES `isVerkorteDag` here (which only covered the "shorter"
  // direction) — a `werkt`-dagdeel LONGER than standard also gets its
  // tijden in the aria-label, even though the sighted view shows no text
  // label for that case (only the duur-balk, per `dagdeelTimeLabel`'s
  // deliberate `null`, AC 15). This keeps the a11y-hard "aria-label bevat
  // altijd de tijden" guarantee independent of the visual label-suppression
  // nuance for that one edge case.
  if (entry.startTime && entry.endTime && (isHalfDay || hasNonStandardSpan(entry))) {
    fragment += `, ${entry.startTime}–${entry.endTime}`;
  }
  return fragment;
}

export function AanwezigheidCell({
  employeeName,
  morning,
  afternoon,
  holidayName,
  isBhv,
  kortVerlofUren,
  kortVerlofStart,
  kortVerlofEind,
}: AanwezigheidCellProps) {
  const merged = sameDagdeel(morning, afternoon);

  // Epic kort-verlof-duur, Story 1.1 (CAP-2): `kortVerlofUren` is a
  // dag-niveau annotation (no dagdeel-positie in the data) — derive the
  // roster-uren from whichever `werkt`-dagdeel actually carries a span
  // (both dagdelen carry the identical full-day-span when kort verlof
  // applies, tech-spec CAP-1, but this stays robust if only one does).
  // Narrowed into a single `kortVerlof` object (instead of a loose
  // `hasKortVerlof` boolean + `kortVerlofUren as number` casts everywhere
  // it's used) so TypeScript proves both values are present below — no
  // `as number` (code-review cleanup).
  //
  // Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 4): alongside
  // the hours, also capture the roster's actual clock-time SPAN (not just its
  // duration) from that same werkt-dagdeel — `kortVerlofPositionBar` needs
  // real "HH:MM" strings, not a derived hour-count. `rosterSpanFor` returns
  // `null` when that dagdeel doesn't carry a usable span, mirroring
  // `rosterHoursFromSpan`'s own guard exactly (same source, same gate).
  const rosterSpan = rosterSpanFor(morning) ?? rosterSpanFor(afternoon);
  const rosterHours = rosterSpan ? rosterHoursFromSpan(rosterSpan.start, rosterSpan.end) : null;
  const kortVerlof =
    kortVerlofUren && kortVerlofUren > 0 && rosterHours && rosterHours > 0
      ? { uren: kortVerlofUren, split: werkVrijSplit(rosterHours, kortVerlofUren) }
      : null;
  // AC-9: valt terug op het bestaande duur-pad (`kortVerlof` hierboven)
  // zodra de positieberekening niet kan — kortVerlofStart/Eind ontbreken
  // (AC-4, verlof zonder tijden) OF de roosterspan zelf ontbreekt (AC-9,
  // "roosterdag waarvan de regel geen begin- of eindtijd heeft"). Geen
  // losse guard nodig: beide gevallen maken `kortVerlofTijdvak` simpelweg
  // `null`, en de overlay hieronder rendert dan het bestaande duur-pad.
  const kortVerlofTijdvak =
    kortVerlof && rosterSpan && kortVerlofStart && kortVerlofEind
      ? {
          start: kortVerlofStart,
          eind: kortVerlofEind,
          bar: kortVerlofPositionBar(rosterSpan.start, rosterSpan.end, kortVerlofStart, kortVerlofEind),
        }
      : null;
  // Review-bevinding 3: bij een gesplitste kantoor/thuis-cel is de
  // werk-segmentkleur de OCHTEND-locatiekleur (gedocumenteerde keuze).
  const werkAccent = morning.location !== 'thuis' ? KANTOOR_ACCENT : THUIS_ACCENT;

  const kortVerlofSuffix = kortVerlofTijdvak
    ? `, ${kortVerlofTijdvakAriaFragment(kortVerlofTijdvak.start, kortVerlofTijdvak.eind)}`
    : kortVerlof
      ? `, ${kortVerlofAriaFragment(kortVerlof.uren)}`
      : '';
  const ariaLabel = merged
    ? `${employeeName}, ${dagdeelAriaFragment(morning)}${kortVerlofSuffix}`
    : `${employeeName}, ochtend ${dagdeelAriaFragment(morning)}, middag ${dagdeelAriaFragment(afternoon)}${kortVerlofSuffix}`;

  // Epic kort-verlof-duur, Story 1.1 (CAP-2, review-bevinding 3): the
  // gestapelde werk+vrij-balk + `Xu vrij`-badge is rendered here, at
  // CEL-niveau, absolutely positioned over the FULL cell width/height —
  // never per-half — so a gesplitste (kantoor/thuis) cel gets the exact
  // same full-width balk as a merged cel.
  //
  // Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (AC-1/AC-3/AC-8):
  // when the tijdvak IS positionable, the balk becomes a full-width
  // werk-accent base with the violette segment absolutely positioned on top
  // at `bar.left`/`bar.width` — this naturally puts werk-kleur on BOTH sides
  // (no separate "left werk-segment" element that could be empty, AC-1
  // rand-geval) and needs no isSplitHalf-style per-segment omission logic.
  // The zichtbare tijdvak-badge follows the SAME uitwijkregel as the
  // per-dagdeel tijd-badge (`isSplitHalf`, AC-3): a gesplitste cel (en
  // daarmee ook AC-8's "twee verloven" — de balk kan dat geval sowieso niet
  // van één lang verlof onderscheiden, ze delen hetzelfde `left`/`width`-
  // datamodel) toont geen zichtbare tekst, de balkpositie + het
  // voorleeslabel dragen dan de betekenis.
  const kortVerlofOverlay = kortVerlofTijdvak ? (
    <>
      <span aria-hidden="true" className="absolute bottom-0 left-0 h-0.5 w-full overflow-hidden" style={{ backgroundColor: werkAccent }}>
        <span
          className="absolute top-0 bottom-0"
          style={{
            left: `${kortVerlofTijdvak.bar.left * 100}%`,
            width: `${kortVerlofTijdvak.bar.width * 100}%`,
            backgroundColor: VRIJ_COLOR,
          }}
        />
      </span>
      {merged && (
        <span className="absolute bottom-0.5 right-1 text-[9px] font-medium leading-none tabular-nums" style={{ color: '#64748b' }}>
          {kortVerlofTijdvakLabel(kortVerlofTijdvak.start, kortVerlofTijdvak.eind)}
        </span>
      )}
    </>
  ) : kortVerlof ? (
    <>
      <span aria-hidden="true" className="absolute bottom-0 left-0 flex h-0.5 w-full overflow-hidden">
        <span style={{ width: `${kortVerlof.split.werkFractie * 100}%`, backgroundColor: werkAccent }} />
        <span style={{ width: `${kortVerlof.split.vrijFractie * 100}%`, backgroundColor: VRIJ_COLOR }} />
      </span>
      <span className="absolute bottom-0.5 right-1 text-[7px] font-medium leading-none" style={{ color: '#64748b' }}>
        {kortVerlofLabel(kortVerlof.uren)}
      </span>
    </>
  ) : null;

  if (merged) {
    return (
      <div role="gridcell" aria-label={ariaLabel} className="relative rounded border border-[#E2E8F0] h-10 overflow-hidden">
        <HalfCell entry={morning} holidayName={holidayName} isBhv={isBhv} suppressDurationVisuals={Boolean(kortVerlof)} />
        {kortVerlofOverlay}
      </div>
    );
  }

  return (
    <div role="gridcell" aria-label={ariaLabel} className="relative rounded border border-[#E2E8F0] h-10 overflow-hidden flex flex-col">
      <div className="h-5">
        <HalfCell entry={morning} holidayName={holidayName} isBhv={isBhv} isSplitHalf suppressDurationVisuals={Boolean(kortVerlof)} />
      </div>
      <div className="h-5 border-t border-dashed border-[#E2E8F0]">
        <HalfCell entry={afternoon} holidayName={holidayName} isBhv={isBhv} isSplitHalf suppressDurationVisuals={Boolean(kortVerlof)} />
      </div>
      {kortVerlofOverlay}
    </div>
  );
}
